const slugify = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const safeText = (value) => String(value ?? "").replace(/_/g, " ").trim();

const ensureMdExtension = (path) =>
  String(path || "").toLowerCase().endsWith(".md") ? path : `${path}.md`;

const isProductionDashboardHost = () => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "dash.zinduaschool.com";
};

const DEFAULT_NON_PROD_LESSON_BUCKET_NAME = "test-folder";
const DEFAULT_NON_PROD_LESSON_PUBLIC_URL_BASE =
  "https://internal-test.zinduaschool.com";

const safeUrlHost = (value) => {
  try {
    return new URL(String(value || "")).host;
  } catch {
    return "";
  }
};

const getNonProdLessonBucketName = () =>
  String(
    import.meta.env.VITE_LESSON_R2_BUCKET_NAME ||
      DEFAULT_NON_PROD_LESSON_BUCKET_NAME,
  ).trim();

const getNonProdLessonPublicUrlBase = () =>
  String(
    import.meta.env.VITE_LESSON_R2_PUBLIC_URL ||
      DEFAULT_NON_PROD_LESSON_PUBLIC_URL_BASE,
  ).trim();

export const getLessonMarkdownUploadTarget = () => {
  if (isProductionDashboardHost()) {
    return {
      bucketName: String(import.meta.env.VITE_BUCKET_NAME || "").trim(),
      publicUrlBase: String(import.meta.env.VITE_R2_PUBLIC_URL || "").trim(),
    };
  }

  return {
    bucketName: getNonProdLessonBucketName(),
    publicUrlBase: getNonProdLessonPublicUrlBase(),
  };
};

export const getLessonMarkdownAllowedHosts = () => {
  const hosts = new Set();

  const envPublicHost = safeUrlHost(import.meta.env.VITE_R2_PUBLIC_URL);
  if (envPublicHost) hosts.add(envPublicHost);

  const nonProdHost = safeUrlHost(getNonProdLessonPublicUrlBase());
  if (nonProdHost) hosts.add(nonProdHost);

  hosts.add("lessons.zinduaschool.com");
  hosts.add("lms-internal.zinduaschool.com");

  return Array.from(hosts);
};

/**
 * Builds the R2 object key for lesson markdown, matching the required structure.
 *
 * Staging/local (anything except dash.zinduaschool.com):
 *   lessons/{courseSlug}/{topicOrder}-{topicTitle}/{lessonOrder}-{lessonTitle}.md
 *
 * Production (dash.zinduaschool.com):
 *   {courseSlug}/{topicOrder}-{topicTitle}/{lessonOrder}-{lessonTitle}.md
 */
export const buildLessonMarkdownObjectKey = ({
  courseSlug,
  topicOrder,
  topicTitle,
  topicId,
  lessonOrder,
  lessonTitle,
}) => {
  const safeCourse = slugify(courseSlug) || "unknown-course";

  const topicOrderText = String(topicOrder ?? "").trim();
  const topicTitleText = safeText(topicTitle);
  const topicTitleSlug = slugify(topicTitleText);

  const topicSegmentBase =
    topicOrderText && topicTitleSlug
      ? `${topicOrderText}-${topicTitleSlug}`
      : topicTitleSlug
      ? topicTitleSlug
      : topicOrderText
      ? topicOrderText
      : `topic-${String(topicId ?? "unknown")}`;

  const lessonOrderText = String(lessonOrder ?? "").trim() || "1";
  const lessonTitleSlug = slugify(safeText(lessonTitle)) || `lesson-${Date.now()}`;
  const lessonSegment = `${lessonOrderText}-${lessonTitleSlug}`;

  const prefix = isProductionDashboardHost() ? "" : "lessons";
  const path = prefix
    ? `${prefix}/${safeCourse}/${topicSegmentBase}/${lessonSegment}`
    : `${safeCourse}/${topicSegmentBase}/${lessonSegment}`;

  return ensureMdExtension(path);
};
